import { useCallback, useMemo } from "react";
import type { z } from "zod";
import getMetricSpec from "../../../merchandising/utils/metrics";
import type { campaignStatsHourlyItemSchema } from "../../../../zodios/apiDefinitions/api/campaignStatsHourly";
import type { CellKey } from "../types";
import { createCellKey } from "../utils";

type CampaignStatsHourlyItem = z.infer<typeof campaignStatsHourlyItemSchema>;

interface UseDaypartingColorsProps {
  data: CampaignStatsHourlyItem[];
  metricId: string;
}

function getCellColor(value: number, min: number, max: number, baseColor: string): string {
  const intensity = max === min ? 0 : (value - min) / (max - min);
  return `color-mix(in srgb, white ${(1 - intensity) * 100}%, ${baseColor})`;
}

export function useDaypartingColors(props: UseDaypartingColorsProps): (hour: number, day: number) => string {
  const { data, metricId } = props;

  const colorMap = useMemo(() => {
    const metricSpec = getMetricSpec(metricId);

    if (data.length === 0) {
      return new Map<CellKey, string>();
    }

    const values = data.map((d) => metricSpec.accessor(d)).filter((v): v is number => v !== null);

    if (values.length === 0) {
      return new Map<CellKey, string>();
    }

    const min = Math.min(...values);
    const max = Math.max(...values);

    const map = new Map<CellKey, string>();
    for (const record of data) {
      const value = metricSpec.accessor(record);
      if (value !== null && record.hour != null && record.weekday != null) {
        const key = createCellKey(record.hour, record.weekday - 1);
        map.set(key, getCellColor(value, min, max, metricSpec.color ?? "#3b82f6"));
      }
    }

    return map;
  }, [data, metricId]);

  return useCallback(
    (hour: number, day: number): string => {
      const key = createCellKey(hour, day);
      return colorMap.get(key) ?? "white";
    },
    [colorMap],
  );
}
